import { PARAGRAPH_BLANK_LINE_MARKER } from './paragraph-markdown';

/**
 * vMDE reconstructs at most this many empty paragraphs from a single newline
 * run. Copied so a pathological saved note cannot explode the MDNE doc.
 */
const MAX_RECONSTRUCTED_BLANK_LINES = 50;

/**
 * A fenced code block (backtick or tilde, CommonMark opening indent ≤3).
 * Unclosed fences are left for marked — this pass must not rewrite inside a
 * closed fence, where extra newlines are source, not blank paragraphs.
 * The closer must not consume the following newline — a `\n{2,}` run after
 * a fence lives in the next prose chunk.
 */
const FENCED_CODE_BLOCK = /^( {0,3})(`{3,}|~{3,})[^\n]*\n[\s\S]*?\n {0,3}\2[ \t]*/gm;

/**
 * Collapse blank line(s) between consecutive same-type list items so a stray
 * blank doesn't split one list into two. Different-type transitions stay.
 * Mirrors `collapseSameTypeListItemGaps` in the vMDE loader.
 */
const collapseSameTypeListItemGaps = (markdown: string): string =>
  markdown
    .replace(/^([ \t]*[-*][ \t]+[^\r\n]*)(?:\r?\n){2,}(?=[ \t]*[-*][ \t]+)/gm, '$1\n')
    .replace(/^([ \t]*\d+\.[ \t]+[^\r\n]*)(?:\r?\n){2,}(?=[ \t]*\d+\.[ \t]+)/gm, '$1\n');

const lineAt = (text: string, offset: number): string => {
  const lineEnd = text.indexOf('\n', offset);
  return text.slice(offset, lineEnd === -1 ? text.length : lineEnd);
};

const lineEndingAt = (text: string, newlineOffset: number): string => {
  const prevBreak = text.lastIndexOf('\n', newlineOffset - 1);
  const lineStart = prevBreak === -1 ? 0 : prevBreak + 1;
  return text.slice(lineStart, newlineOffset);
};

/**
 * GFM table row (two or more cells, or a delimiter). A one-cell pipe line
 * (`| just text |`) is prose in both editors — treating it as a table would
 * drop an extra blank that vMDE reconstructs (N-1) while MDNE used N-2.
 */
const isGfmTableLine = (line: string): boolean => {
  const trimmed = line.replace(/^ {0,3}/, '');
  if (!trimmed.startsWith('|')) {
    return false;
  }
  if (/^\|[\t :|-]*-[\t :|-]*\|/.test(trimmed)) {
    return true;
  }
  return (trimmed.match(/\|/g) ?? []).length >= 3;
};

/**
 * Block starters that already own a CommonMark `\n\n` separator. Promoting
 * those runs would insert an empty paragraph under every MDNE heading, list,
 * table, or `<!-- section-end -->` marker.
 */
const isStructuralBlockLine = (line: string): boolean =>
  /^( {0,3}(#{1,6}(?: |\t|$)|([-*+]|\d+[.)])(?: |\t|$)|[>]|(`{3,}|~{3,})|<!--[\s\S]*-->[ \t]*$))/.test(
    line
  ) || isGfmTableLine(line);

const isBlankMarkerLine = (line: string): boolean => line === PARAGRAPH_BLANK_LINE_MARKER;

/**
 * Generated-note "headings" are a bold-only paragraph, not `# Title`. vMDE
 * reconstructs the `\n\n` after the heading and before the next one as empty
 * paragraphs; MDNE must too.
 */
const isBoldOnlyLine = (line: string): boolean => /^\*\*[^*\n]+\*\*[ \t]*$/.test(line);

/**
 * True when the text after a newline run is a section heading (`# `, up to
 * three leading spaces). Positional, not a tail-slice regex — this pass runs
 * on every note load.
 */
const findSectionHeadingStart = (text: string, offset: number): number =>
  /^( {0,3}#{1,6}(?: |\t|$))/.test(lineAt(text, offset)) ? offset : -1;

/**
 * Promote extra newline runs to MDNE `&nbsp;` empty-paragraph lines.
 *
 * vMDE stores k empty paragraphs as (k+1) newlines between content blocks
 * (and k+2 before a `# ` heading, which owns a 2-newline separator). marked
 * collapses those runs, so MDNE loading vMDE-saved notes drops the gaps.
 *
 * A single CommonMark `\n\n` is left alone except when either adjacent line
 * is bold-only (`**Presenting Complaint**` after the heading, or before the
 * next generated heading). vMDE reconstructs those as empty paragraphs.
 * Promoting every `\n\n` would insert blanks under MDNE `# ` sections and
 * drift round-trips that serialize sibling paragraphs as `\n\n`.
 *
 * MDNE `&nbsp;` separators must not be promoted again (fixed point).
 */
const promoteNewlineRuns = (markdown: string): string =>
  markdown.replace(/\n{2,}/g, (run: string, offset: number, full: string) => {
    const after = offset + run.length;
    const prevLine = lineEndingAt(full, offset);
    const nextLine = lineAt(full, after);
    const ownedSeparator =
      findSectionHeadingStart(full, after) !== -1 ||
      isStructuralBlockLine(nextLine) ||
      isStructuralBlockLine(prevLine) ||
      isBlankMarkerLine(nextLine) ||
      isBlankMarkerLine(prevLine);
    let emptyParagraphs: number;
    if (ownedSeparator) {
      emptyParagraphs = Math.max(0, run.length - 2);
    } else if (run.length === 2) {
      emptyParagraphs = isBoldOnlyLine(prevLine) || isBoldOnlyLine(nextLine) ? 1 : 0;
    } else {
      emptyParagraphs = run.length - 1;
    }
    const markers = `${PARAGRAPH_BLANK_LINE_MARKER}\n\n`.repeat(
      Math.min(emptyParagraphs, MAX_RECONSTRUCTED_BLANK_LINES)
    );
    return `\n\n${markers}`;
  });

const rewriteOutsideFences = (
  markdown: string,
  rewrite: (chunk: string) => string
): string => {
  FENCED_CODE_BLOCK.lastIndex = 0;
  let last = 0;
  let out = '';
  let match: RegExpExecArray | null;
  while ((match = FENCED_CODE_BLOCK.exec(markdown)) !== null) {
    out += rewrite(markdown.slice(last, match.index));
    out += match[0];
    last = match.index + match[0].length;
  }
  out += rewrite(markdown.slice(last));
  return out;
};

const rewriteProseChunk = (chunk: string): string => {
  const stripped = chunk.replace(/^[^\S\n]+$/gm, '');
  return promoteNewlineRuns(collapseSameTypeListItemGaps(stripped));
};

/**
 * Pre-lex rewrite so vMDE-saved blank-line runs hydrate in MDNE. Idempotent:
 * MDNE-saved `&nbsp;` markdown is unchanged. Applied inside the markdown-native
 * marked Lexer so every parse path (editor load, paste, canonical renderer)
 * sees the same vocabulary.
 *
 * Whitespace strip, list-gap collapse, and newline promotion all run only on
 * prose chunks; closed fences are copied through unchanged. CRLF is folded to
 * LF first so Windows-saved vMDE notes take the same path as LF.
 */
export const normalizeLegacyBlankLines = (markdown: string): string =>
  rewriteOutsideFences(markdown.replace(/\r\n?/g, '\n'), rewriteProseChunk);
